import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// T171 (03.11): wylogowanie po 30 min bezczynności — u admina i każdego z uprawnieniem (lustro needsIdleLogout
// w backend/src/utils/session.js). Token takiej osoby jest ważny 30 min i ma pole `idle`; klik / klawisz / dotyk
// przedłuża go (POST /auth/refresh, najwyżej raz na 5 min). Minutę przed końcem okno „Jesteś tam?”.
// Czas liczymy z tokenu w localStorage, więc kilka kart i uśpiony laptop działają tak samo.
const PRIVILEGE_FIELDS = ['canApproveHours', 'canApproveLeaves', 'canSettleLeaves', 'canManageEvents', 'canAssignEvents',
  'canSettlePayroll', 'canApproveEventHours', 'canOrderSupplies', 'canAccounting'];
const needsIdleLogout = (u) => u?.role === 'ADMIN' || PRIVILEGE_FIELDS.some(f => u?.[f]);
const tokenPayload = () => {
  try {
    const t = localStorage.getItem('funhr_token');
    return t ? JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) : null;
  } catch { return null; }
};
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'wheel'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idleLeft, setIdleLeft] = useState(null); // T171: sekundy do wylogowania (okno „Jesteś tam?”)

  useEffect(() => {
    if (!user) return undefined;
    let busy = false;
    const refresh = () => {
      if (busy) return;
      busy = true;
      api.refreshSession().catch(() => {}).finally(() => { busy = false; setIdleLeft(null); });
    };
    // Token sprzed T171 (7 dni) u kogoś z uprawnieniami — od razu zamieniamy na sesję 30-minutową
    const first = tokenPayload();
    if (first && !first.idle && needsIdleLogout(user)) refresh();
    const onActivity = () => {
      const p = tokenPayload();
      if (!p?.idle) return;
      const left = p.exp * 1000 - Date.now();
      if (left > 0 && left < (p.idle - 5) * 60000) refresh();
    };
    const tick = () => {
      const p = tokenPayload();
      if (!p?.idle) { setIdleLeft(null); return; }
      const left = p.exp * 1000 - Date.now();
      if (left <= 0) {
        localStorage.removeItem('funhr_token');
        localStorage.removeItem('funhr_user');
        window.location.href = '/login?bezczynnosc=1';
        return;
      }
      setIdleLeft(left <= 60000 ? Math.ceil(left / 1000) : null);
    };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    const t = setInterval(tick, 1000);
    return () => { clearInterval(t); ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity)); };
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('funhr_token');
    const saved = localStorage.getItem('funhr_user');
    if (token && saved) {
      setUser(JSON.parse(saved));
      // Refresh user data from server
      api.me().then(u => {
        setUser(u);
        localStorage.setItem('funhr_user', JSON.stringify(u));
      }).catch(() => {
        localStorage.removeItem('funhr_token');
        localStorage.removeItem('funhr_user');
        setUser(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    localStorage.setItem('funhr_token', data.token);
    localStorage.setItem('funhr_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  // Odświeżenie danych zalogowanego z serwera (saldo urlopu, uprawnienia) - T20
  const refreshUser = async () => {
    try {
      const u = await api.me();
      setUser(u);
      localStorage.setItem('funhr_user', JSON.stringify(u));
      return u;
    } catch { return null; }
  };

  const logout = () => {
    localStorage.removeItem('funhr_token');
    localStorage.removeItem('funhr_user');
    setUser(null);
  };

  const isAdmin = user?.role === 'ADMIN';
  const isCoordinator = user?.role === 'COORDINATOR';
  const isWorker = ['WAREHOUSE', 'EVENT', 'ADMINISTRATION'].includes(user?.role);
  const isAdministration = user?.role === 'ADMINISTRATION';

  // Prawo zatwierdzania wynika z osobnych pól, nie z roli.
  const canApproveHours = isAdmin || !!user?.canApproveHours;
  const canApproveLeaves = isAdmin || !!user?.canApproveLeaves;
  // Rozlicza urlopy: salda wszystkich + zatwierdzone wnioski + przycisk "Rozliczono" (pole, nie rola)
  const canSettleLeaves = isAdmin || !!user?.canSettleLeaves;
  // T69: eventy mają własne uprawnienia — tworzenie/edycja i samo przypisywanie ludzi
  const canManageEvents = isAdmin || !!user?.canManageEvents;
  const canAssignEvents = canManageEvents || !!user?.canAssignEvents;
  // T87: ekran „Rozliczenia" (wypłaty)
  const canSettlePayroll = isAdmin || !!user?.canSettlePayroll;
  // T110: zamawia zapotrzebowanie (pole, nie rola)
  const canOrderSupplies = isAdmin || !!user?.canOrderSupplies;
  // T113: „Kadry i księgowość” — stawki wszystkich (podgląd), pula urlopu, dni i ewidencja pracy zdalnej (T114)
  const canAccounting = isAdmin || !!user?.canAccounting;
  // T118 (16.10): eventy i dostępność zespołu widzi tylko ktoś z uprawnieniem eventowym — samo zatwierdzanie godzin
  // (np. koordynator magazynu) ich nie daje; własne eventy — kto sam na nie jeździ
  // T124: „Mój zespół” — ktoś ma podwładnych
  const hasTeam = (user?._count?.subordinates || 0) > 0;
  // T127: kalendarz nieobecności pracowników stałych — admin, kadry, rozliczający urlopy
  const seesStaffAbsences = isAdmin || !!user?.canAccounting || !!user?.canSettleLeaves || !!user?.canApproveLeaves; // T129: koordynator — swój zespół
  const seesTeamEvents = isAdmin || !!user?.canManageEvents || !!user?.canAssignEvents || !!user?.canApproveEventHours;
  // T86: pracownik tylko eventowy — uproszczony panel (bez Godzin i Zapotrzebowania)
  const isEventOnly = user?.role === 'EVENT';
  // T77: opiekun eventowych — zatwierdza godziny i wydatki eventowe WSZYSTKICH (+ dokumenty, T81)
  const canApproveEventHours = isAdmin || !!user?.canApproveEventHours;
  // Ktokolwiek rozpatruje cudze godziny/dodatki (zakładka „Zespół", przyciski ✓ ✗)
  const canReviewHours = canApproveHours || canApproveEventHours;
  const isManagement = canApproveHours || canApproveLeaves || canApproveEventHours;

  // Urlop nie przysługuje np. pracownikom eventowym na zleceniu.
  const hasLeave = user?.hasLeaveEntitlement !== false;
  // T97: wniosek o pracę zdalną — Biuro (w tym handlowcy) i koordynatorzy; lustro REMOTE_ROLES w routes/leaves.js
  const canRemoteWork = ['ADMINISTRATION', 'COORDINATOR'].includes(user?.role);

  // Godziny i dodatki wpisuje magazynowy, eventowy albo ktokolwiek z włączoną pracą eventową.
  // Lustro allowedWorkTypes w backend/routes/hours.js (T32).
  // T76: także „Praca na magazynie" (np. Biuro pomagające przy rozładunku)
  const canAddHours = ['WAREHOUSE', 'EVENT'].includes(user?.role) || !!user?.eventWorkEnabled || !!user?.warehouseWorkEnabled;
  // T151/T155: „Dodatki do rozliczenia” — wszyscy poza eventowym (on zgłasza z „Moich eventów”); admin — do zatwierdzania.
  // Kto nie wpisuje godzin, zgłasza tylko wydatki / transport / inne (EXPENSE_ONLY_TYPES)
  const hasExpenses = user?.role !== 'EVENT';
  // T154 (19.10): „+ Godziny” w Godzinach pracy — tylko praca na magazynie; eventy rozlicza się po evencie w „Eventy”
  const canAddWarehouseHours = user?.role === 'WAREHOUSE' || !!user?.warehouseWorkEnabled;
  // T90/T91 (decyzja 08.10): specyfikacja wypłat i karta eventów innych osób
  const canSeePay = isAdmin || !!user?.canSettlePayroll || !!user?.canApproveEventHours;
  // T57: kalendarz dostępności — eventowi i osoby z pracą eventową (admin nie deklaruje)
  const hasAvailability = !isAdmin && (user?.role === 'EVENT' || !!user?.eventWorkEnabled);
  // T47: eventowy bez uzupełnionych danych nie wejdzie dalej niż formularz
  const needsOnboarding = user?.role === 'EVENT' && !user?.profileCompletedAt;
  // T172: hasło ustawione przez admina — przy logowaniu trzeba ustawić własne
  const needsPasswordChange = !!user?.mustChangePassword;
  // T46: kto może wysyłać wiadomości; T54: kto zgłasza dokumenty eventowe
  const canSendMessages = isAdmin || !!user?.canApproveHours || !!user?.canApproveLeaves || !!user?.canApproveEventHours;
  const hasEventDocs = !isAdmin && (user?.role === 'EVENT' || !!user?.eventWorkEnabled);

  return (
    <AuthContext.Provider value={{
      user, login, logout, refreshUser, loading,
      isAdmin, isCoordinator, isWorker, isAdministration,
      canApproveHours, canApproveLeaves, canSettleLeaves, isManagement, hasLeave, canAddHours, hasAvailability, needsOnboarding, needsPasswordChange, canSendMessages, hasEventDocs, canManageEvents, canAssignEvents, canSettlePayroll, canOrderSupplies, canAccounting, seesTeamEvents, hasTeam, seesStaffAbsences, isEventOnly, canApproveEventHours, canReviewHours, canSeePay, canRemoteWork, hasExpenses, canAddWarehouseHours,
    }}>
      {children}
      {idleLeft !== null && (
        <div className="modal-overlay" style={{ zIndex: 3000 }}>
          <div className="modal modal-sm" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28 }}>⏳</div>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 8px' }}>Jesteś tam?</h3>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 14 }}>
              Nic się nie działo od prawie 30 minut. Za <b>{idleLeft} s</b> wylogujemy Cię, żeby nikt inny nie działał na Twoim koncie.
            </div>
            <button className="btn btn-primary" onClick={() => api.refreshSession().catch(() => {}).finally(() => setIdleLeft(null))}>Zostaję zalogowany</button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
